import React, { useEffect, useState, useRef } from 'react';
import { BrowserRouter as Router, useNavigate, useLocation } from 'react-router-dom';
import Main from './components/Layout/main';
import { ToastContainer } from 'react-toastify';
import { UserProvider } from "./context/UserContext";
import { TenantProvider } from "./context/TenantContext";
import { ActiveFederationProvider } from "./context/ActiveFederationContext";
// Subscribe & Zoho subscribe - commented out
// import { SubscriptionProvider } from "./context/SubscriptionContext";
import SecureCookieManager from './utils/secureCookie';
import { getV2Token } from './utils/v2AuthUtils';
import { validateToken } from './utils/tokenValidator';
import socketService from './utils/socketService';
import logoutService from './services/logoutService';

import './styles/_variables.css';
import './styles/app.css';
import './styles/theme.css';
import './i18n'
import InactivityHandler from './components/Layout/InActivityHandler';
import i18n from './i18n';
import { getCurrentLanguage } from './utils/languageUtils';
import AutoTenantLanguage from './components/AutoTenantLanguage';

const AppContent = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [userId, setUserId] = useState<string | null>(null);
    const socketInitialized = useRef(false);

    useEffect(() => {
        // Authentication is now handled by ProtectedRoute component
        // This effect only handles initial token sync from v2-frontend cookies
        const syncTokenFromV2 = async () => {
            // pathname is relative to basename, so '/login' is correct
            // Also treat '/compliance/login' as a login page and skip token sync
            if (
              location.pathname === '/login' ||
              location.pathname === '/register' ||
              location.pathname === '/compliance/login'
            ) {
                return; // Allow access to login/register pages
            }

            // Check for token from v2-frontend cookies and sync it
            const v2Token = getV2Token();
            
            if (v2Token) {
                console.log('[front_compliance] Found token from v2-frontend cookies, syncing...');
                // Validate token client-side (same as new_demo_frontend)
                const userData = await validateToken(v2Token);
                if (userData) {
                    // Only set token cookie if it doesn't already exist to avoid duplicates
                    const existingToken = SecureCookieManager.getAuthToken();
                    if (!existingToken || existingToken !== v2Token) {
                        SecureCookieManager.setAuthToken(v2Token);
                    }
                    console.log('[front_compliance] Token synced successfully');
                } else {
                    console.warn('[front_compliance] Token validation failed');
                }
            }
        };

        // Sync token on mount (with small delay to ensure cookies are available after optimization)
        const timeoutId = setTimeout(() => {
            syncTokenFromV2();
        }, 100);

        return () => {
            clearTimeout(timeoutId);
        };
    }, [location.pathname]);

    // Accept auth token from hash (e.g. from new_demo_frontend "Compliance Management System" link) for shared login
    useEffect(() => {
        if (typeof window === 'undefined') return;
        const hash = window.location.hash;
        const match = hash && hash.startsWith('#auth=') && hash.slice(6);
        if (match) {
            try {
                const token = decodeURIComponent(match);
                if (token) {
                    SecureCookieManager.setAuthToken(token);
                    const path = location.pathname || '/';
                    window.history.replaceState(null, '', path);
                }
            } catch (e) {
                // ignore invalid hash
            }
        }
    }, [location.pathname]);

    // When another tab runs logout-clear, go to login so both apps stay in sync
    useEffect(() => {
        let channel: BroadcastChannel | null = null;
        try {
            channel = new BroadcastChannel('cross-app-logout-compliance');
            channel.onmessage = () => {
                SecureCookieManager.clearAuthCookies();
                localStorage.clear();
                window.location.href = '/login';
            };
        } catch {
            /* BroadcastChannel not supported */
        }
        return () => { channel?.close(); };
    }, []);

    // Initialize socket connection when user is authenticated (same as landing)
    // useEffect(() => {
    //     const initializeSocket = () => {
    //         const token = getV2Token();
    //         if (token) {
    //             const userData = validateToken(token);
    //             const currentUserId = userData?.id?.toString();
                
    //             if (currentUserId && currentUserId !== userId) {
    //                 setUserId(currentUserId);
                    
    //                 // Disconnect existing socket if any
    //                 if (socketInitialized.current) {
    //                     socketService.disconnect();
    //                 }
                    
    //                 socketInitialized.current = true;

    //                 // Handle force logout from server
    //                 const handleForceLogout = async () => {
    //                     await logoutService.logout();
    //                     window.location.href = '/login';
    //                 };

    //                 // Connect socket
    //                 socketService.connect(currentUserId, handleForceLogout);
    //             } else if (!currentUserId && socketInitialized.current) {
    //                 // User logged out, disconnect socket
    //                 socketService.disconnect();
    //                 socketInitialized.current = false;
    //                 setUserId(null);
    //             }
    //         } else if (socketInitialized.current) {
    //             // No token, disconnect socket
    //             socketService.disconnect();
    //             socketInitialized.current = false;
    //             setUserId(null);
    //         }
    //     };

    //     // Check periodically for user authentication
    //     const checkAuth = setInterval(() => {
    //         initializeSocket();
    //     }, 2000);

    //     // Listen for login events
    //     const handleUserLoggedIn = () => {
    //         initializeSocket();
    //     };
    //     window.addEventListener('userLoggedIn', handleUserLoggedIn);

    //     // Initial check
    //     initializeSocket();

    //     return () => {
    //         clearInterval(checkAuth);
    //         window.removeEventListener('userLoggedIn', handleUserLoggedIn);
    //         socketService.disconnect();
    //         socketInitialized.current = false;
    //     };
    // }, [userId]);

    return <Main />;
};
const currentLanguage = getCurrentLanguage();
document.documentElement.lang = currentLanguage;
document.documentElement.dir = currentLanguage === 'ar' ? 'rtl' : 'ltr';

function App() {
    return (
        <UserProvider>
            <TenantProvider>
                <ActiveFederationProvider>
                {/* Subscribe & Zoho subscribe - commented out
                <SubscriptionProvider>
                */}
                <Router>
                        <ToastContainer autoClose={5000} hideProgressBar />
                        <AppContent />
                        <AutoTenantLanguage />
                        <InactivityHandler/>
                    </Router>
                {/* </SubscriptionProvider> */}
                </ActiveFederationProvider>
            </TenantProvider>
        </UserProvider>
    );
}

export default App;
